'use client';

import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import {
  Badge,
  DrawerCustomSection,
  DrawerTable,
  Text,
} from '@terra/ui-design-system';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { AI_COMPONENT_TYPE_KEYS } from '../utils/aiComponentTypeLabels';

type RelatedComponentRow = {
  id: string;
  name: string;
  typeLabel: string;
};

export function RelatedAiComponentsDrawerTable({
  relatedComponents,
}: {
  relatedComponents: AiComponentDto[];
}) {
  const t = useTranslations('aiApplications.componentDrawer');
  const tShared = useTranslations('aiApplications.agentDrilldown');
  const tAssets = useTranslations('aiApplications.assets');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');

  const rowData = useMemo(
    () =>
      relatedComponents.map((relatedComponent) => ({
        id: relatedComponent.id,
        name: relatedComponent.name,
        typeLabel: tComponentTypes(
          AI_COMPONENT_TYPE_KEYS[relatedComponent.type],
        ),
      })),
    [relatedComponents, tComponentTypes],
  );

  const columnDefs = useMemo<ColDef<RelatedComponentRow>[]>(
    () => [
      {
        headerName: tAssets('assetName'),
        field: 'name',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<RelatedComponentRow>) => (
          <Text fontSize="sm" color="fg.default">
            {params.value ?? ''}
          </Text>
        ),
      },
      {
        headerName: tShared('type'),
        field: 'typeLabel',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<RelatedComponentRow>) => (
          <Badge variant="outline">{params.value ?? ''}</Badge>
        ),
      },
    ],
    [tAssets, tShared],
  );

  if (rowData.length === 0) {
    return (
      <DrawerCustomSection title={t('relatedComponents')}>
        <Text fontSize="sm" color="fg.muted">
          {t('noRelatedComponents')}
        </Text>
      </DrawerCustomSection>
    );
  }

  return (
    <DrawerTable
      title={t('relatedComponents')}
      rowData={rowData}
      columnDefs={columnDefs}
    />
  );
}
